import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { useAppSelector } from '../../../store/redux/hooks'
import {
  CollapseDirection,
  CollapseState,
  SidebarLinkItem,
  SidebarSectionKey,
} from '../../../store/types/components/sidebar/sidebarTypes'
import { SidebarSection } from '../sidebarUtils'
import { SIDEBAR_SECTION_DEFINITIONS } from '../sidebarSectionConfig'

const sectionOrder = Object.fromEntries(SIDEBAR_SECTION_DEFINITIONS.map((def, i) => [def.key, i])) as Record<
  SidebarSection,
  number
>

export const useSidebarIndicator = (
  sidebarLinks: SidebarLinkItem[],
  visibleLinks: SidebarLinkItem[],
  isSidebarOpen: boolean
) => {
  const pageIndex = useAppSelector(state => state.pageName.index)
  const selectedIdx = visibleLinks.findIndex(item => item.link.index === pageIndex)
  const linkRefs = useRef<{ [index: number]: HTMLDivElement | null }>({})
  const indicatorRef = useRef<HTMLDivElement>(null)
  const indicatorAnimationRef = useRef<number | null>(null)
  const indicatorFreezeRef = useRef<{ top: number; height: number } | null>(null)
  const [collapseKey, setCollapseKey] = useState(0)
  const [collapseState, setCollapseState] = useState<CollapseState>({
    section: null,
    direction: null,
    isActive: false,
  })

  const isCollapsing = collapseState.isActive
  const currentSection = sidebarLinks.find(l => l.link.index === pageIndex)?.section

  const isBlockingCollapse =
    collapseState.section != null &&
    currentSection != null &&
    sectionOrder[collapseState.section] > sectionOrder[currentSection]

  const isIndicatorFading =
    collapseState.direction === 'exit' && collapseState.section != null && collapseState.section === currentSection

  const freezeIndicatorPosition = useCallback(() => {
    const selectedRef = linkRefs.current[pageIndex]
    if (!selectedRef) return
    indicatorFreezeRef.current = {
      top: selectedRef.offsetTop,
      height: selectedRef.offsetHeight,
    }
  }, [pageIndex])

  const handleCollapseStart = useCallback(
    (section: SidebarSectionKey, direction: NonNullable<CollapseDirection>) => {
      if (direction === 'exit') freezeIndicatorPosition()
      setCollapseState({ section, direction, isActive: true })
    },
    [freezeIndicatorPosition]
  )

  const handleCollapseComplete = useCallback(() => {
    setCollapseKey(k => k + 1)
    setCollapseState({ section: null, direction: null, isActive: false })
    indicatorFreezeRef.current = null
  }, [])

  const updateIndicatorPosition = useCallback(
    (shouldAnimate: boolean) => {
      if (isBlockingCollapse) return
      const selectedRef = linkRefs.current[pageIndex]
      const indicator = indicatorRef.current
      if (!indicator) return

      if (selectedIdx === -1 || !selectedRef) {
        indicator.style.transition = 'opacity 200ms ease'
        indicator.style.opacity = '0'
        return
      }

      if (isIndicatorFading) {
        indicator.style.transition = 'opacity 200ms ease'
        const frozen = indicatorFreezeRef.current
        if (frozen) {
          indicator.style.transform = `translateY(${frozen.top}px)`
          indicator.style.height = `${frozen.height}px`
        }
        indicator.style.opacity = '0'
        return
      }

      indicator.style.transition = shouldAnimate
        ? 'transform 300ms cubic-bezier(0.4,0,0.2,1), height 200ms cubic-bezier(0.4,0,0.2,1), opacity 200ms'
        : 'none'
      indicator.style.opacity = '1'
      indicator.style.transform = `translateY(${selectedRef.offsetTop}px)`
      indicator.style.height = `${selectedRef.offsetHeight}px`
    },
    [isIndicatorFading, isBlockingCollapse, pageIndex, selectedIdx]
  )

  useLayoutEffect(() => {
    if (isBlockingCollapse) return

    const stopAnimation = () => {
      if (indicatorAnimationRef.current !== null) {
        cancelAnimationFrame(indicatorAnimationRef.current)
        indicatorAnimationRef.current = null
      }
    }

    if (isIndicatorFading) {
      stopAnimation()
      updateIndicatorPosition(true)
      return () => stopAnimation()
    }

    if (isCollapsing) {
      const animate = () => {
        updateIndicatorPosition(false)
        indicatorAnimationRef.current = requestAnimationFrame(animate)
      }
      animate()
      return () => stopAnimation()
    }

    stopAnimation()
    updateIndicatorPosition(true)
    return () => stopAnimation()
  }, [
    updateIndicatorPosition,
    selectedIdx,
    pageIndex,
    isSidebarOpen,
    visibleLinks.length,
    collapseKey,
    isCollapsing,
    isIndicatorFading,
    isBlockingCollapse,
  ])

  return {
    linkRefs,
    indicatorRef,
    isCollapsing,
    handleCollapseStart,
    handleCollapseComplete,
  }
}
